import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Loader2, TrendingUp, ShieldCheck, Users } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      if (u?.must_change_password) nav("/change-password");
      else if (u?.role === "admin" || u?.role === "super_admin") nav("/bots");
      else nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-background terminal-bg">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden border-r border-border">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/tmc-logo.png" alt="The Money Club" className="w-12 h-12 rounded-xl border border-primary/40" />
          <div><div className="font-head font-extrabold text-white text-lg tracking-wide"><span className="gold-text">THE MONEY</span> CLUB</div><div className="text-[10px] text-accent/80 font-mono tracking-wider">AKADEMIA · SYGNAŁY · SPOŁECZNOŚĆ</div></div>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-5xl font-extrabold text-white leading-tight">Ucz się, handluj,<br />rośnij z <span className="gold-text">The Money Club</span>.</h1>
          <p className="text-gray-400 mt-6 max-w-md">Akademia tradingu, sygnały rynkowe i społeczność, która uczy Cię handlować świadomie — od podstaw po kompletny framework TMC.</p>
          <div className="flex flex-col gap-2 mt-8 text-sm">
            <span className="flex items-center gap-2 text-gray-300"><TrendingUp className="w-4 h-4 text-accent" /> Sygnały na XAUUSD, indeksy i forex</span>
            <span className="flex items-center gap-2 text-gray-300"><ShieldCheck className="w-4 h-4 text-accent" /> Akademia, zarządzanie ryzykiem i psychologia</span>
            <span className="flex items-center gap-2 text-gray-300"><Users className="w-4 h-4 text-accent" /> Aktywna społeczność traderów</span>
          </div>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground font-mono">Handel niesie wysokie ryzyko straty kapitału.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="flex flex-col items-center gap-2 mb-6 lg:hidden">
            <img src="/tmc-logo.png" alt="The Money Club" className="w-16 h-16 rounded-xl border border-primary/40" />
            <span className="font-head font-extrabold text-white text-lg">THE MONEY CLUB</span>
          </div>
          <h2 className="font-head text-2xl font-bold text-white">Witaj ponownie</h2>
          <p className="text-sm text-muted-foreground mb-6">Zaloguj się do swojego terminala</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground font-mono">E-mail</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email-input"
                className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-mono">Hasło</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="login-password-input"
                className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary transition-colors" />
            </div>
            {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full bg-primary hover:opacity-90 text-black font-bold rounded-lg px-4 py-2.5 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Zaloguj się
            </button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Nie masz konta?{" "}
            <button onClick={() => nav("/register")} data-testid="login-register-link" className="gold-text hover:underline font-medium">Zarejestruj się</button>
          </p>
          <p className="text-[11px] text-muted-foreground mt-4 text-center">
            <button onClick={() => nav("/privacy")} data-testid="login-privacy-link" className="hover:text-white underline">Polityka prywatności</button>
            <span className="mx-2">·</span>
            <button onClick={() => nav("/cookies")} data-testid="login-cookies-link" className="hover:text-white underline">Polityka cookies</button>
          </p>
        </div>
      </div>
    </div>
  );
}
